import React, { useContext, useState, useEffect } from "react";
import { navigate } from "gatsby";
import { useLocation } from "@reach/router";
import GlobalContext from "../../context/GlobalContext";
import { useUserPlan } from "../../context/UserPlanContext";
import { useAuth } from "../../context/AuthContext";
import Logo from "../Logo";
import SidebarDashboard2 from "../SidebarDashboard2/SidebarDashboard2";

const Sidebar = () => {
  const gContext = useContext(GlobalContext);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { visibleSidebarItems, canAccessDashboard } = useUserPlan();
  // Normalize pathname (strip trailing slash) so /dashboard and /dashboard/ both match
  const path = (location && location.pathname && location.pathname.replace(/\/$/, "")) || "";
  const isDemoDashboard = path === "/dashboard";

  // Map view ids to routes (used for navigation from SidebarDashboard2)
  const viewToPath = {
    tender: "/dashboard",
    result: "/dashboard-result",
    "boq-lead": "/dashboard-boq-lead",
    "tender360-dashboard": "/dashboard-main",
    "tender360-bidder-profile": "/dashboard-bidder-profile",
    "tender360-competitor-matrix": "/dashboard-competitor-matrix",
    "tender360-missed-opportunities": "/dashboard-missed-opportunities",
    "tender360-lost-tender-evaluation": "/dashboard-lost-tender-evaluation",
    "tender360-rate-analysis": "/dashboard-l1-l2-comparison",
    "tender360-department-insights": "/dashboard-department-insights",
    "tender360-state-statistics": "/dashboard-state-statistics",
    "tender360-analytical-report": "/dashboard-analytical-report",
    "tender-bidding": "/dashboard-tender-bidding",
    calendar: "/dashboard-calendar",
    "gem-registration": "/gem-registration",
    "apex-expert-connect": "/apex-expert-connect",
  };
  // Map path to activeView so SidebarDashboard2 highlights the current page
  const pathToView = {
    "/dashboard": "tender",
    "/dashboard-result": "result",
    "/dashboard-boq-lead": "boq-lead",
    "/dashboard-main": "tender360-dashboard",
    "/dashboard-bidder-profile": "tender360-bidder-profile",
    "/dashboard-competitor-matrix": "tender360-competitor-matrix",
    "/dashboard-missed-opportunities": "tender360-missed-opportunities",
    "/dashboard-lost-tender-evaluation": "tender360-lost-tender-evaluation",
    "/dashboard-l1-l2-comparison": "tender360-rate-analysis",
    "/dashboard-department-insights": "tender360-department-insights",
    "/dashboard-state-statistics": "tender360-state-statistics",
    "/dashboard-analytical-report": "tender360-analytical-report",
    "/dashboard-tender-bidding": "tender-bidding",
    "/dashboard-calendar": "calendar",
    "/dashboard-tender360": "tender360-dashboard",
    "/gem-registration": "gem-registration",
    "/apex-expert-connect": "apex-expert-connect",
  };
  const activeViewFromPath = pathToView[path] || "tender";
  const handleNavigate = (view) => {
    const to = viewToPath[view];
    if (to) navigate(to);
  };

  // When on dashboard, some items navigate to their own page instead of inline view
  const handleViewChange = (view) => {
    if (view === "tender360-dashboard") {
      handleNavigate(view);
      return;
    }
    if (isDemoDashboard) {
      setActiveView(view);
    } else {
      handleNavigate(view);
    }
  };

  // State management for dashboard sidebar – persist across refresh via sessionStorage
  const [activeView, setActiveView] = useState(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("dashboard-view") || "tender";
    }
    return "tender";
  });

  // On mount/hydration, sync activeView from sessionStorage when on dashboard (fixes refresh)
  useEffect(() => {
    if (isDemoDashboard && typeof window !== "undefined") {
      const stored = sessionStorage.getItem("dashboard-view");
      if (stored) setActiveView(stored);
    }
  }, [isDemoDashboard]);

  useEffect(() => {
    if (isDemoDashboard && typeof window !== 'undefined') {
      sessionStorage.setItem('dashboard-view', activeView);
      // Dispatch event immediately and also use a small timeout to ensure it's caught
      window.dispatchEvent(new CustomEvent('dashboard-view-change'));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('dashboard-view-change'));
      }, 10);
    }
  }, [activeView, isDemoDashboard]);

  // Listen for changes from dashboard page
  useEffect(() => {
    if (isDemoDashboard && typeof window !== 'undefined') {
      const handleViewChange = () => {
        const newView = sessionStorage.getItem('dashboard-view');
        if (newView && newView !== activeView) {
          setActiveView(newView);
        }
      };
      window.addEventListener('dashboard-view-change', handleViewChange);
      window.addEventListener('storage', handleViewChange);
      return () => {
        window.removeEventListener('dashboard-view-change', handleViewChange);
        window.removeEventListener('storage', handleViewChange);
      };
    }
  }, [activeView, isDemoDashboard]);

  // Use SidebarDashboard2 for all dashboard pages (same UI everywhere)
  const currentActiveView = isDemoDashboard ? activeView : activeViewFromPath;

  return (
    <>
      <SidebarDashboard2
        showSidebar={gContext.showSidebarDashboard}
        activeView={currentActiveView}
        onViewChange={handleViewChange}
        visibleSidebarItems={visibleSidebarItems}
        canAccessDashboard={canAccessDashboard}
        isAuthenticated={isAuthenticated}
      />
      <a
        href="/#"
        className="sidebar-mobile-button"
        aria-expanded={gContext.showSidebarDashboard}
        onClick={(e) => {
          e.preventDefault();
          gContext.toggleSidebarDashboard();
        }}
      >
        <i className="icon icon-sidebar-2"></i>
      </a>
    </>
  );
};

export default Sidebar;
